import { z as zod } from 'zod';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';

import {
    Box,
    Card,
    Stack,
    Radio,
    Button,
    Dialog,
    Typography,
    RadioGroup,
    DialogTitle,
    DialogActions,
    DialogContent,
    CircularProgress,
    FormControlLabel,
} from '@mui/material';

import { toast } from 'src/components/snackbar';
import { Form } from 'src/components/hook-form';
import { Iconify } from 'src/components/iconify';

const selectCompetitionSchema = zod.object({
    selectedCompetitionId: zod.number().int().min(1, { message: 'Competition selection is required!' }),
});

export default function SelectCompetitionDialog({
    open,
    onClose,
    onSave,
    activeCompetitions = [],
    currentSelectedCompetitionId,
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);



    const methods = useForm({
        mode: 'onChange',
        resolver: zodResolver(selectCompetitionSchema),
        defaultValues: {
            selectedCompetitionId: currentSelectedCompetitionId || 0,
        }
    });

    const {
        reset,
        watch,
        setValue,
        handleSubmit,
        formState: { isValid },
    } = methods;

    const selectedCompetitionId = watch('selectedCompetitionId');

    // Update form when current selected competition changes
    useEffect(() => {
        if (currentSelectedCompetitionId) {
            setValue('selectedCompetitionId', currentSelectedCompetitionId);
        }
    }, [currentSelectedCompetitionId, setValue]);

    const handleCancel = () => {
        reset();
        onClose();
    };

    const handleSaveClick = async () => {
        if (!isValid || selectedCompetitionId === currentSelectedCompetitionId) {
            return;
        }

        try {
            setIsSubmitting(true);
            await onSave(selectedCompetitionId);
            reset();
            onClose();
            toast.success('Competition selection updated successfully!');
        } catch (error) {
            console.error('Error updating selected competition:', error);
            toast.error(error.message || 'An error occurred while updating the competition selection.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (!open) {
            reset();
        } else if (currentSelectedCompetitionId) {
            setValue('selectedCompetitionId', currentSelectedCompetitionId);
        }
    }, [open, reset, currentSelectedCompetitionId, setValue]);

    const hasChanges = selectedCompetitionId !== currentSelectedCompetitionId;
    const selectedCompetition = activeCompetitions.find(comp => comp.Id === selectedCompetitionId);

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="eva:settings-2-fill" width={24} />
                    <Typography variant="h6">Select Competition</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Form methods={methods} onSubmit={handleSubmit(handleSaveClick)}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {activeCompetitions.length > 1 
                                ? 'Choose your active competition from the available options below:' 
                                : 'Current active competition details:'
                            }
                        </Typography>

                        <Card sx={{ p: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                {activeCompetitions.length > 1 ? 'Available Competitions' : 'Selected Competition'}
                            </Typography>

                            {activeCompetitions.length > 0 ? (
                                <RadioGroup
                                    value={selectedCompetitionId}
                                    onChange={(e) => setValue('selectedCompetitionId', parseInt(e.target.value, 10))}
                                >
                                    {activeCompetitions.map((competition) => (
                                        <FormControlLabel
                                            key={competition.Id}
                                            value={competition.Id}
                                            control={<Radio />}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body1" fontWeight={600}>
                                                                {competition.Name}
                                                            </Typography>
                                                            {!competition.IsActive && (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        px: 1,
                                                                        py: 0.25,
                                                                        bgcolor: 'error.lighter',
                                                                        borderRadius: 0.75,
                                                                        border: '1px solid',
                                                                        borderColor: 'error.main',
                                                                    }}
                                                                >
                                                                    <Iconify 
                                                                        icon="eva:close-circle-fill" 
                                                                        width={12} 
                                                                        sx={{ color: 'error.main' }}
                                                                    />
                                                                    <Typography 
                                                                        variant="caption" 
                                                                        sx={{ 
                                                                            color: 'error.dark',
                                                                            fontWeight: 600,
                                                                            fontSize: '0.675rem'
                                                                        }}
                                                                    >
                                                                        INACTIVE
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        {competition.CompetitionType && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Type: {competition.CompetitionType.Name}
                                                            </Typography>
                                                        )}
                                                        {competition.Semester && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Semester: {competition.Semester.Name}
                                                                {competition.Semester.AcademicYear && 
                                                                    ` (${competition.Semester.AcademicYear.Name})`
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            }
                                            sx={{
                                                border: 1,
                                                borderColor: selectedCompetitionId === competition.Id ? 'primary.main' : 'divider',
                                                borderRadius: 1,
                                                p: 2,
                                                mb: 1,
                                                bgcolor: 'transparent',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No active competitions available
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Debug: activeCompetitions.length = {activeCompetitions.length}
                                    </Typography>
                                </Box>
                            )}
                        </Card>

                        {selectedCompetition && hasChanges && (
                            <Box sx={{ p: 2, bgcolor: 'primary.lighter', borderRadius: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify icon="eva:info-fill" width={16} color="primary.main" />
                                    <Typography variant="body2" color="primary.main">
                                        You have selected: <strong>{selectedCompetition.Name}</strong>
                                    </Typography>
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </Form>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button 
                    onClick={handleCancel} 
                    color="inherit" 
                    variant="outlined"
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSaveClick} 
                    variant="contained" 
                    disabled={(!hasChanges && activeCompetitions.length > 1) || isSubmitting || !isValid}
                    sx={{ position: 'relative' }}
                >
                    {isSubmitting && (
                        <CircularProgress
                            size={24}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={1}
                        sx={{ opacity: isSubmitting ? 0 : 1 }}
                    >
                        <Iconify icon="eva:checkmark-circle-2-fill" />
                        <span>{activeCompetitions.length > 1 ? 'Update Selection' : 'Close'}</span>
                    </Stack>
                </Button>
            </DialogActions>
        </Dialog>
    );
}
